import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { noteSchema } from "../schema";
import { NoteType } from "../types";
import { useAddOrUpdateNoteMutation } from "../api/mutations";
import useEncounterStore from "../store/encounter-store";
import { toast } from "sonner";
import useAddNoteStore from "../store/add-note-store";
import { Note } from "../components/form/note-form";
import { patientLogsKeys } from "../api/queries/keys";
import { tasksKeys } from "~/features/dashboard/api/tasks/keys";
import { useQueryClient } from "@tanstack/react-query";

type useNoteProps = {
    data?: Note;
    patientId: string;
  };
  
  export const useNote = ({ data, patientId }: useNoteProps) => {
    const queryClient = useQueryClient();
    const { encounterId } = useEncounterStore();
    const { closeNoteModal } = useAddNoteStore();
    const [isRefetching, setIsRefetching] = useState(false);
  
    const [value, setValue] = useState({
      subjective: data?.subjective ?? "",
      objective: data?.objective ?? "",
      assessment: data?.assessment ?? "",
      plan: data?.plan ?? "",
    });
  
    const form = useForm<NoteType>({
      resolver: zodResolver(noteSchema),
      defaultValues: data ?? {
        subjective: "",
        objective: "",
        assessment: "",
        plan: "",
      },
    });
  
    const addNoteMutation = useAddOrUpdateNoteMutation();
  
    const handleSubmit = (data: NoteType) => {
      const payload = {
        encounterId: encounterId || "",
        subjective: data.subjective,
        objective: data.objective,
        assessment: data.assessment,
        plan: data.plan,
        patientId: patientId
      };
  
      addNoteMutation.mutate(payload, {
        onSuccess: async () => {
          toast.success("Note added successfully!");
        //   logger.log("patient", patientId)
          setIsRefetching(true);
          // Wait for queries to refetch before closing the modal
          try {
            await queryClient.refetchQueries({
              queryKey: patientLogsKeys.notes(patientId),
              exact: false,
              type: "active",
            });
            if (encounterId) {
              await queryClient.refetchQueries({
                queryKey: tasksKeys.getTasksByEncounterId(encounterId),
                exact: false,
                type: "active",
              });
            }
            closeNoteModal();
          } catch (error) {
            // Even if refetch fails, close the modal after a short delay
            setTimeout(() => {
              closeNoteModal();
            }, 500);
          } finally {
            setIsRefetching(false);
          }
        },
        onError: () => {
          toast.error("Failed to add note");
        },
      });
      setValue(data);
    };
    return {
      form,
      value,
      handleSubmit,
      isLoading: addNoteMutation.isPending || isRefetching,
    };
  };
  
  export default useNote;
  